import type {
  ServiceCardTypeItems,
  ServiceMetaItem,
} from "@/components/services/service.card.types";
import type { HotelLandscapeCardTypeItems } from "@/components/card/HotelLandscapeCard";
import type { HotelCardTypeItems } from "@/components/card/HotelCard";
import type { TourActCarouselItem } from "@/components/main-services/tour-act/tour.type";
import type { RentalCarouselItem } from "@/components/main-services/rentals/rental.type";
import type { SpaListingCaruselItem } from "@/components/main-services/spa-msg/spa-msg.type";
import {
  HotelSearchResult,
  RentalSearchResult,
  ResultCardData,
  SpaSearchResult,
  TourSearchResult,
} from "@/components/search-results/search.result.types";
import { formatDistance } from "@/utils/format/distance";

/**
 * Hotel:
 * Mapper for both: Search Result & View on Map
 * (Using the same card)
 */
export const mapSearchHotel = (
  result: HotelSearchResult,
): HotelLandscapeCardTypeItems => {
  return {
    id: result.id,
    title: result.title,
    primary: result.image,
    totalBooking: result.totalBooking,
    starRating: result.starRating,
    rating: result.rating,
    reviewCount: result.reviewCount,
    hasBreakfast: result.hasBreakfast,
    cancelPolicy: result.cancelPolicy,
    isInstantConfirm: result.isInstantConfirm,
    specs: result.specs,
    distance: result.distance,
    distanceFormatted: result.distanceFormatted,
    attributes: result.attributes,
    price: result.price,
    salePrice: result.salePrice,
    disPercent: result.disPercent,
    availMsg: result.availMsg,
    isFavorite: result.isFavorite,
  };
};

/**
 * Hotel:
 * Mapper for portrait HotelCard (e.g., Book for Me results & Similar Hotels)
 */
export const mapSearchHotelCard = (
  result: HotelSearchResult | ResultCardData,
): HotelCardTypeItems => {
  const item = result as Record<string, any>;
  const rating = Number(
    result.rating ?? item.overallReview?.overallRating ?? 0,
  );
  const reviewCount =
    result.reviewCount ?? item.mbcTotalReviews ?? item.totalBooking ?? null;

  return {
    id: Number(result.id) || 0,
    feature: item.feature ?? item.primary ?? result.image ?? null,
    title: result.title ?? "",
    price: Number(result.price) || null,
    salePrice: result.salePrice ? Number(result.salePrice) : null,
    isFavorite: Boolean(result.isFavorite),
    isInstantConfirm: Boolean(result.isInstantConfirm),
    overallReview:
      rating > 0
        ? {
            overallRating: rating,
            maxScore: 5,
            ratingPerCriteria: [],
            totalReviews: reviewCount != null ? Number(reviewCount) : null,
          }
        : null,
    starRating: Number(item.starRating) || null,
    hasBreakfast: item.hasBreakfast ? 1 : 0,
    cancellationNote:
      item.cancellationNote ?? item.cancelPolicy?.message ?? null,
    located:
      item.located ??
      item.location?.address ??
      result.distanceFormatted ??
      (typeof item.distance === "string" ? item.distance : null),
    disPercent: result.disPercent ? Number(result.disPercent) : null,
    availMsg: String(item.availMsg ?? ""),
    mbcTotalReviews: reviewCount != null ? Number(reviewCount) : null,
    propertyType: item.propertyType ?? { name: "Hotel" },
  };
};

/**
 * Renal: Mapper for View on Map
 * (Not the same card as Search Result)
 */
export const mapSearchRentalForMapView = (
  result: RentalSearchResult,
): ServiceCardTypeItems => {
  const metaItems: ServiceMetaItem[] = [];

  if (result.specs && result.specs.length > 0) {
    if (result.specs[0]) {
      metaItems.push({
        icon: "helmet",
        text: result.specs[0],
      });
    }
    if (result.specs[1]) {
      metaItems.push({
        icon: "fuel",
        text: result.specs[1],
      });
    }
  }

  if (result.distance) {
    metaItems.push({
      icon: "location",
      text: result.distance,
    });
  }

  return {
    id: String(result.id),
    section: "rental",
    title: result.title,
    imageSrc: result.image,
    rating: result.rating,
    reviewCount: result.reviewCount,
    isInstantConfirm: result.isInstantConfirm,
    isFavorite: result.isFavorite,
    quickIcons: result.quickIcons,
    metaItems,
    price: result.price,
    salePrice: result.salePrice,
  };
};

/**
 * Rental: Mapper for Search Result
 * (Not the same card as View on Map)
 */
export const mapSearchRentalCard = (
  result: RentalSearchResult,
): RentalCarouselItem => {
  const item = result as Record<string, any>;

  return {
    ...item,
    id: Number(result.id) || 0,
    title: result.title,
    feature: item.feature ?? result.image,
    located: item.located ?? result.distance ?? "-",
    price: String(result.price ?? 0),
    salePrice: String(result.salePrice ?? 0),
    disPercent: result.disPercent ?? null,
    isFavorite: Boolean(result.isFavorite),
    isInstantConfirm: Boolean(result.isInstantConfirm),
    availMsg: item.availMsg ?? "",
    mchtName: item.mchtName ?? item.merchant?.fullName ?? "",
    totalReviews: result.reviewCount ?? null,
    overallReview: result.rating
      ? { overallRating: result.rating, maxScore: 5, ratingPerCriteria: [] }
      : null,
    attributes: (result.attributes as any) ?? [],
  } as unknown as RentalCarouselItem;
};

/**
 * Spa & Massage: Mapper for View on Map
 * (Not the same card as Search Result)
 */
export const mapSearchSpaForMapView = (
  result: SpaSearchResult,
): ServiceCardTypeItems => {
  return {
    id: String(result.id),
    section: "spa_msg",
    title: result.title,
    imageSrc: result.feature,
    rating: result.merchant?.avgRating ?? 0,
    reviewCount: result.merchant?.totalReview ?? 0,
    isInstantConfirm: result.isInstantConfirm,
    isFavorite: result.isFavorite,
    metaItems: [
      ...(result.serviceName
        ? [{ icon: "massage" as const, text: result.serviceName }]
        : []),
      ...(result.duration
        ? [{ icon: "clock" as const, text: result.duration }]
        : []),
      ...(result.distance
        ? [
            {
              icon: "location" as const,
              text: formatDistance(
                result.distance,
                result.distanceUnit,
                result.distanceMeasurement,
              ),
            },
          ]
        : []),
    ],
    price: result.price,
    salePrice: result.salePrice,
  };
};

/**
 * Spa & Massage: Mapper for Search Result
 * (Not the same card as View on Map)
 */
export const mapSearchSpaCard = (
  result: SpaSearchResult,
): SpaListingCaruselItem => {
  const item = result as Record<string, any>;
  const rating =
    result.merchant?.avgRating ??
    item.overallReview?.overallRating ??
    item.rating ??
    0;
  const totalReviews =
    result.merchant?.totalReview ??
    item.totalReviews ??
    item.reviewCount ??
    null;

  return {
    ...item,
    id: Number(result.id) || 0,
    title: result.title,
    feature: result.feature ?? item.image ?? null,
    located:
      item.located ??
      (result.distance
        ? `${result.distance} ${result.distanceUnit ?? "km"}`
        : "-"),
    price: Number(result.price) || 0,
    salePrice: Number(result.salePrice) || 0,
    disPercent: result.disPercent ?? null,
    isFavorite: Boolean(result.isFavorite),
    isInstantConfirm: Boolean(result.isInstantConfirm),
    duration: result.duration ? String(result.duration) : null,
    totalReviews,
    overallReview: rating
      ? { overallRating: Number(rating), maxScore: 5 }
      : null,
  } as SpaListingCaruselItem;
};

/**
 * Tour & Activity: Mapper for View on Map
 * (Not the same card as Search Result)
 */
export const mapSearchTourForMapView = (
  result: TourSearchResult,
): ServiceCardTypeItems => {
  const durationText = result.duration
    ? String(result.duration).includes("hour")
      ? String(result.duration)
      : `${result.duration} hours`
    : "";

  return {
    id: String(result.id),
    section: "tour_act",
    title: result.title,
    imageSrc: result.feature,
    rating: result.merchant?.avgRating ?? 0,
    reviewCount: result.merchant?.totalReview ?? 0,
    isInstantConfirm: result.isInstantConfirm,
    isFavorite: result.isFavorite,
    metaItems: [
      ...(result.category?.name
        ? [{ icon: "telescope" as const, text: result.category.name }]
        : []),
      ...(durationText ? [{ icon: "clock" as const, text: durationText }] : []),
      ...(result.distance
        ? [
            {
              icon: "location" as const,
              text: formatDistance(
                result.distance,
                result.distanceUnit,
                result.distanceMeasurement,
              ),
            },
          ]
        : []),
    ],
    price: Number(result.price) || 0,
    salePrice: Number(result.salePrice) || 0,
  };
};

/**
 * Tour & Activity: Mapper for Search Result
 * (Not the same card as View on Map)
 */
export const mapSearchTourCard = (
  result: TourSearchResult,
): TourActCarouselItem => {
  const anyResult = result as Record<string, any>;
  const durationText = result.duration
    ? String(result.duration).includes("hour") ||
      String(result.duration).includes("min")
      ? String(result.duration)
      : `${result.duration} hours`
    : null;

  const rawDist = result.distance;
  const numDist = typeof rawDist === "number" ? rawDist : Number(rawDist) || 0;

  const located =
    (typeof anyResult.located === "string" ? anyResult.located : null) ??
    anyResult.location?.address ??
    (typeof anyResult.address === "string" ? anyResult.address : null) ??
    (numDist > 0
      ? formatDistance(
          numDist,
          result.distanceUnit ?? "km",
          result.distanceMeasurement ?? "city_center",
        )
      : "");

  const rating =
    result.merchant?.avgRating ??
    anyResult.overallReview?.overallRating ??
    anyResult.overallReview?.rating ??
    anyResult.rating ??
    null;

  const totalReviews =
    result.merchant?.totalReview ??
    anyResult.overallReview?.totalReviews ??
    anyResult.reviewCount ??
    null;

  return {
    id: Number(result.id) || 0,
    title: result.title,
    provinceId: Number(anyResult.provinceId ?? 0),
    feature: result.feature ?? anyResult.image ?? null,
    located,
    duration: durationText,
    price: String(result.price ?? "0"),
    salePrice: String(result.salePrice ?? "0"),
    disPercent: String(result.disPercent ?? "0"),
    isFavorite: Boolean(result.isFavorite),
    isInstantConfirm: Boolean(result.isInstantConfirm),
    totalReviews: totalReviews != null ? Number(totalReviews) : null,
    overallReview:
      rating != null
        ? {
            overallRating: Number(rating),
            ratingPerCriteria: [],
            totalReviews: totalReviews != null ? Number(totalReviews) : 0,
          }
        : null,
  } as TourActCarouselItem;
};
